import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Link as MuiLink,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from "../datepicker/CommonDatePicker";
import { format } from "date-fns";
import config from "../common/Config";
import "../common/commonStyles.css";
import { buildDatatablePayload, postDashboardDatatable } from "../../utils/dashboardApi";

const OrderAccepted = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState('');

  const s = localStorage.getItem('startDate');
  const e = localStorage.getItem('endDate');

  const formatDate = (date) => {
    if (!date) return "0";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const startOfMonth = new Date(year, month, 1);
    const startDay = String(startOfMonth.getDate()).padStart(2, "0");
    const startMonth = String(startOfMonth.getMonth() + 1).padStart(2, "0");

    const endOfMonth = new Date(year, month + 1, 0);
    const endDay = String(endOfMonth.getDate()).padStart(2, "0");
    const endMonth = String(endOfMonth.getMonth() + 1).padStart(2, "0");

    const startDateFormatted = `${startDay}-${startMonth}-${year}`;
    const endDateFormatted = `${endDay}-${endMonth}-${year}`;

    return { startDateFormatted, endDateFormatted };
  };

  const { startDateFormatted, endDateFormatted } = getCurrentMonthDates();

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'yyyy-MM-dd');
      const formattedEndDate = format(value[1], 'yyyy-MM-dd');
      localStorage.setItem('startDate', formattedStartDate);
      localStorage.setItem('endDate', formattedEndDate);
      setReportrange(`${formattedStartDate} - ${formattedEndDate}`);
      setPage(0);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(
        token,
        'get_order_accepted',
        buildDatatablePayload({
          page,
          rowsPerPage,
          startDate: s,
          endDate: e,
          searchValue,
        })
      );

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      }
    } catch (error) {
      console.error('Error fetching order accepted data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, s, e, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }
    const timeoutId = setTimeout(() => {
      if (searchValue !== '') {
        setPage(0);
      }
      fetchData();
    }, searchValue ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchData, searchValue]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSubmit = () => {
    setPage(0);
    fetchData();
  };

  const formatRowDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <Box className="page-container" >
          <Paper
            className="paper-style"
          >
            <Box sx={{ px:2, py:1.2}}>
              <Grid2 container spacing={1} justifyContent="space-between" alignItems="center">
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Typography className="page-title">
                    Order Accepted
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md:3}}>
                  <TextField
                    className="input-border"
                    size="small"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-fill" sx={{ color: '#212b36', width: 20, height: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md:3}}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
             
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    className="button-variant-contained"
                    fullWidth
                  >
                    Submit
                  </Button>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 3}} display="flex" justifyContent={{xs:"center", md:"flex-end"}} alignItems="center">
                  <Typography
                    
                    sx={{ color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.875rem" }, whiteSpace: 'nowrap' }}>
                    From: {s ? formatDate(s) : startDateFormatted} &nbsp;
                    To: {e ? formatDate(e) : endDateFormatted}
                  </Typography>
                </Grid2>
              </Grid2>
            </Box>

              <TableContainer
              >
                {loading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={40} />
                  </Box>
                )}
                <Table
                  size="medium"
                  className="table-cell-border"
                >
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell
                        width="100px"
                      >
                        Order Date
                      </TableCell>
                      <TableCell
                        width="80px"
                      >
                        Order Id
                      </TableCell>
                      <TableCell
                        width="200px"
                      >
                        Customers (A/C No)
                      </TableCell>
                      <TableCell
                        width="100px"
                      >
                        Postal Code
                      </TableCell>
                      <TableCell
                        width="100px"
                        align="right" 
                      >
                        Order Amt
                      </TableCell>
                      <TableCell
                        align="right"
                        width="100px"
                      >
                        Rep Cash
                      </TableCell>
                      <TableCell
                        align="right"
                        width="100px"
                      >
                        C.Bal
                      </TableCell>
                      <TableCell
                        width="100px"
                      >
                        Route
                      </TableCell>
                      <TableCell
                        width="100px"
                      >
                        Order By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && data && data.length > 0 ? (
                      data.map((row, index) => (
                        <TableRow
                          key={index}
                          hover
                          className="table-cell-style"
                        >
                          <TableCell
                          >
                            {formatRowDate(row.date)}
                          </TableCell>
                          <TableCell
                          >
                            <MuiLink
                              href={`${config.BASE_URL}/sales/edit/${row.ids}`}
                              sx={{
                                color: '#212b36',
                              }}
                            >
                              {row.reference_no}
                            </MuiLink>
                          </TableCell>
                          <TableCell
                          >
                            {row.customer}
                          </TableCell>
                          <TableCell
                          >
                            {row.postal_code}
                          </TableCell>
                          <TableCell
                            align="right"
                          >
                            {row.grand_total}
                          </TableCell>
                          <TableCell
                            align="right"
                          >
                            {row.spc}
                          </TableCell>
                          <TableCell
                            align="right"
                          >
                            {row.prev_due}
                          </TableCell>
                          <TableCell
                          >
                            {row.route_number}
                          </TableCell>
                          <TableCell
                          >
                            {row.name}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : !loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          sx={{ py: 8 }}
                        >
                          <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                            No data found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>

              {!loading && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  className="pagination-styles"
                />
              )}
          </Paper>
        </Box>
    </>
  );
};

export default OrderAccepted;
